import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CalendarClock, Plus, Trash2, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TYPES = [
  ['corte', 'Corte de cliente'],
  ['pago_licencia', 'Pago de licencia'],
  ['reunion', 'Reunión'],
  ['fidelizacion', 'Fidelización'],
  ['nota', 'Nota'],
];
const COLOR = { corte: 'bg-red-100 text-red-700', pago_licencia: 'bg-green-100 text-green-700', reunion: 'bg-blue-100 text-blue-700', fidelizacion: 'bg-purple-100 text-purple-700', nota: 'bg-gray-100 text-gray-700' };

export default function MasterAgenda() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [f, setF] = useState({ title: '', date: '', time: '', type: 'corte', tenant_id: '', notes: '' });

  const load = () => { api.getMasterAgenda().then((r) => setItems(r.data)).catch(() => {}); };
  useEffect(() => { load(); api.getTenants().then((r) => setTenants(r.data)).catch(() => {}); }, []);

  const add = async () => {
    if (!f.title.trim() || !f.date) return toast.error('SalesPRO: Indica título y fecha');
    try { await api.createMasterAgenda(f); toast.success('SalesPRO: Marcado en tu agenda privada'); setF({ ...f, title: '', time: '', notes: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };
  const del = async (id) => { try { await api.deleteMasterAgenda(id); load(); } catch { toast.error('SalesPRO: Error'); } };
  const tName = (id) => tenants.find((t) => t.tenant_id === id)?.name;

  if (user?.role_id !== 'master') return <div className="p-6 text-[#64748B]" data-testid="agenda-denied">Acceso exclusivo del Super Master.</div>;

  return (
    <div className="space-y-5" data-testid="master-agenda-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#022C22] flex items-center gap-2"><CalendarClock /> Agenda Privada del Super Master</h1>
        <p className="text-sm text-[#64748B] flex items-center gap-1"><Lock size={13} /> Solo tú (Super Master) ves esta agenda: cortes, reuniones, fidelización y control de tus clientes de licencia.</p>
      </div>

      <Card><CardContent className="p-4 grid md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-2"><Label>Título *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Ej. Corte Celular Beach" data-testid="agenda-title" /></div>
        <div><Label>Fecha *</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="agenda-date" /></div>
        <div><Label>Hora</Label><Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
        <div>
          <Label>Tipo</Label>
          <select className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="agenda-type">
            {TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label>Empresa (opcional)</Label>
          <select className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white" value={f.tenant_id} onChange={(e) => setF({ ...f, tenant_id: e.target.value })}>
            <option value="">—</option>
            {tenants.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-5"><Label>Notas</Label><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Detalles privados" /></div>
        <Button onClick={add} className="bg-[#022C22]" data-testid="agenda-add"><Plus size={15} className="mr-1" /> Marcar</Button>
      </CardContent></Card>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-[#94A3B8] text-center py-6">Tu agenda privada está vacía.</p>}
        {items.map((it) => (
          <div key={it.item_id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex justify-between items-center gap-2" data-testid={`agenda-item-${it.item_id}`}>
            <div>
              <p className="font-semibold text-[#022C22] text-sm flex items-center gap-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${COLOR[it.type] || COLOR.nota}`}>{(TYPES.find((t) => t[0] === it.type) || [])[1] || it.type}</span>
                {it.title}
              </p>
              <p className="text-xs text-[#64748B]">{it.date}{it.time ? ` · ${it.time}` : ''}{it.tenant_id ? ` · ${tName(it.tenant_id) || ''}` : ''}{it.notes ? ` · ${it.notes}` : ''}</p>
            </div>
            <Button size="icon" variant="ghost" onClick={() => del(it.item_id)} data-testid={`agenda-del-${it.item_id}`}><Trash2 size={16} className="text-red-500" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
